import React, { Fragment, PureComponent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    CalculatorsConfigItemsType,
    CalculatorsConfigSectionSettingsItem,
    VitalsSelectedItemsType,
} from '../../../../../api/pushGenie';
import { DEFAULT_CALCULATOR_CONFIG_VALUES } from '../../../../../constants';
import { clearVitals, updateVitals } from '../../../../../store/actions/prescriptionPad';
import { upsertCalculatorConfig, upsertVitalsConfig } from '../../../../../store/actions/session';
import { GVitals } from '../../../../../store/db';
import { getGLocal } from '../../../../../store/idbHandler';
import { getCalcultorPadConfig, getVitalsPadConfig } from '../../../../../store/selectors/session';
import { VitalsEntity } from '../../../../../store/types/prescription';
import { SlideFromRightWrapper } from '../../../../common/SlideFromRightWrapper';
import CalculatorConfigPanel from './CalculatorsConfigPanel';
import { removeUnwatedKeys, VitalAutoSuggest } from './VitalAutoSuggest';
import { DB_TABLES } from '../../../../../store/dbTables';
import { getMyDbId } from '../../../../../api/mactiveid';

const SELECTED_VITALS_ITEMS = 'SELECTED_VITALS_ITEMS';

const PPVitalsConfigPanel: React.FC<{ closeMe: () => void }> = ({ closeMe }) => {
    const dispatch = useDispatch();
    const vitalConfig = useSelector(getVitalsPadConfig);
    const calculatorConfig = useSelector(getCalcultorPadConfig);
    const [selectedTab, setSelectedTab] = useState<'vitals' | 'calculator'>('vitals');
    const [vitalsCommonItems, setVitalsCommonItems] = useState<VitalsSelectedItemsType[]>(
        vitalConfig?.selectedItems || [],
    );
    const [calculatorsConfig, setCalculatorsConfig] = useState<
        CalculatorsConfigSectionSettingsItem[]
    >([] as CalculatorsConfigSectionSettingsItem[]);
    const [deletedVitals, setDeletedVitals] = useState<string[]>([]);

    const setVitalsCommonItemsSectionData = (option: VitalsEntity) => {
        const isDuplicateItemPresent = vitalsCommonItems.find((ele) => ele.name === option.name);
        if (!isDuplicateItemPresent) {
            setVitalsCommonItems([...vitalsCommonItems, { id: option.id, name: option.name }]);
        }
        const containerId = document.getElementById(SELECTED_VITALS_ITEMS) as HTMLDivElement;
        containerId.scrollTo({
            top: containerId?.scrollHeight + 10000,
            behavior: 'smooth',
        });
    };

    function deleteVitalsCommonItemsElement(id: string) {
        const data = vitalsCommonItems.filter((e) => e.id !== id);
        if (data) {
            const deletedVital = vitalsCommonItems.find((d) => d.id === id);
            if (deletedVital) {
                setDeletedVitals((prev) => [...prev, deletedVital.id]);
            }
        }
        setVitalsCommonItems(data);
    }

    useEffect(() => {
        const sectionItemsKeysSet = new Set(calculatorConfig?.selectedItems?.map((r) => r.key));

        const mergedSectionAndDefaultConfig = [
            ...new Map(
                [
                    ...(calculatorConfig?.selectedItems || []),
                    ...DEFAULT_CALCULATOR_CONFIG_VALUES.selectedItems,
                ].map((item) => [item['key'], item]),
            ).values(),
        ];

        setCalculatorsConfig(
            mergedSectionAndDefaultConfig?.map((i) => {
                return {
                    ...i,
                    isShown: sectionItemsKeysSet.has(i.key),
                };
            }) || [],
        );
    }, []);

    const onSave = async (): Promise<void> => {
        dispatch(clearVitals(deletedVitals));

        dispatch(
            upsertVitalsConfig({
                selectedItems: vitalsCommonItems,
            }),
        );

        const calculatorsConfigData: CalculatorsConfigItemsType[] = calculatorsConfig
            .filter((d) => d.isShown)
            .map((d) => ({
                label: d.label,
                type: 'string',
                key: d.key,
            }));

        dispatch(
            upsertCalculatorConfig({
                selectedItems: calculatorsConfigData,
            }),
        );

        closeMe();
    };

    const selectVitals = () => {
        setSelectedTab('vitals');
    };

    const selectCalculator = () => {
        setSelectedTab('calculator');
    };

    return (
        <SlideFromRightWrapper closeMe={closeMe} cssClass="w-70p">
            <div className="bg-darwin-neutral-50 flex flex-col h-full space-between w-full">
                <div className="flex items-center space-x-16 p-16 pb-24 cursor-pointer">
                    <div onClick={closeMe} className="flex items-center justify-center w-16 h-16">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 448 512"
                            className="w-16 h-16 fill-darwin-neutral-1000"
                        >
                            <path d="M447.1 256c0 13.25-10.76 24.01-24.01 24.01H83.9l132.7 126.6c9.625 9.156 9.969 24.41 .8125 33.94c-9.156 9.594-24.34 9.938-33.94 .8125l-176-168C2.695 268.9 .0078 262.6 .0078 256S2.695 243.2 7.445 238.6l176-168C193 61.51 208.2 61.85 217.4 71.45c9.156 9.5 8.812 24.75-.8125 33.94l-132.7 126.6h340.1C437.2 232 447.1 242.8 447.1 256z"></path>
                        </svg>
                    </div>
                    <p className="text-darwin-neutral-1000 heading-2 bold">Customise vitals</p>
                </div>

                <div className="w-full pl-8 border-b border-neutral-8 select-none">
                    <div className="flex items-center font-600 text-14 leading-28 cursor-pointer ml-8">
                        <div
                            className={
                                selectedTab === 'vitals'
                                    ? 'border-b-2 border-actionPrimary  text-14 font-700'
                                    : 'border-b-2 border-transparent text-14 font-700'
                            }
                            onClick={selectVitals}
                        >
                            <p
                                className={
                                    selectedTab === 'vitals'
                                        ? 'pb-8 text-actionPrimary text-14 font-700'
                                        : 'pb-8 text-neutral-3 text-14 font-700'
                                }
                            >
                                Vitals Config
                            </p>
                        </div>
                        <div
                            className={
                                selectedTab === 'calculator'
                                    ? 'border-b-2 mx-16 border-actionPrimary  text-14 font-700'
                                    : 'border-b-2 mx-16 border-transparent text-14 font-700'
                            }
                            onClick={selectCalculator}
                        >
                            <p
                                className={
                                    selectedTab === 'calculator'
                                        ? 'pb-8 text-actionPrimary text-14 font-700'
                                        : 'pb-8 text-neutral-3 text-14 font-700'
                                }
                            >
                                Calculators Config
                            </p>
                        </div>
                    </div>
                </div>

                {selectedTab === 'vitals' && (
                    <VitalsSection
                        setVitalsCommonItemsSectionData={setVitalsCommonItemsSectionData}
                        vitalsCommonItems={vitalsCommonItems}
                        deleteVitalsCommonItemsElement={deleteVitalsCommonItemsElement}
                    />
                )}

                {selectedTab === 'calculator' && (
                    <CalculatorConfigPanel
                        calculatorsConfig={calculatorsConfig}
                        setCalculatorsConfig={setCalculatorsConfig}
                    />
                )}
                <div className="flex items-center justify-between absolute rounded-8 bg-actionPrimary bottom-24 inset-x-10 px-16 py-8 cta-row">
                    <button onClick={closeMe} className="text-btn-v2 px-16 py-8 btn--small">
                        Cancel
                    </button>
                    <button
                        className="px-16 py-8 btn--small primary-btn-v2 tooltip-wrapper"
                        onClick={onSave}
                    >
                        <div className="flex items-center justify-center w-16 h-16">
                            <svg
                                className="w-16 h-16 fill-actionPrimary"
                                role="img"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 448 512"
                            >
                                <path d="M264.6 70.63l176 168c4.75 4.531 7.438 10.81 7.438 17.38s-2.688 12.84-7.438 17.38l-176 168c-9.594 9.125-24.78 8.781-33.94-.8125c-9.156-9.5-8.812-24.75 .8125-33.94l132.7-126.6H24.01c-13.25 0-24.01-10.76-24.01-24.01s10.76-23.99 24.01-23.99h340.1l-132.7-126.6C221.8 96.23 221.5 80.98 230.6 71.45C239.8 61.85 254.1 61.51 264.6 70.63z"></path>
                            </svg>
                        </div>

                        <p>Save Changes</p>
                    </button>
                </div>
            </div>
        </SlideFromRightWrapper>
    );
};

interface VitalsSectionProps {
    setVitalsCommonItemsSectionData: (option: VitalsEntity) => void;
    vitalsCommonItems: VitalsSelectedItemsType[];
    deleteVitalsCommonItemsElement: (id: string) => void;
}

class VitalsSection extends PureComponent<VitalsSectionProps> {
    render() {
        const {
            setVitalsCommonItemsSectionData,
            vitalsCommonItems,
            deleteVitalsCommonItemsElement,
        } = this.props;
        return (
            <Fragment>
                <div className="p-16 flex flex-col lg:max-h-400 max-h-462 space-y-16">
                    <div className="flex h-full rounded-8 bg-white lg:flex-row flex-col">
                        <div className="flex p-16 lg:mb-0 mb-16 flex-col flex-1">
                            <p className="text-12 text-darwin-neutral-1000 whitespace-nowrap">
                                SELECT VITALS
                            </p>
                            <VitalAutoSuggest
                                onSelect={setVitalsCommonItemsSectionData}
                                elements={vitalsCommonItems}
                            />
                        </div>
                        <div className="flex relative pt-0 flex-col border-t-1 border-darwin-neutral-100 lg:w-40p lg:border-darwin-neutral-100 lg:border-l-1  overflow-auto w-full">
                            <p
                                className="text-12 text-darwin-neutral-1000 whitespace-nowrap py-12 bg-white absolute top-0 w-full text-center"
                                style={{ boxShadow: '0 1px 4px 0 rgb(0 0 0 / 40%)' }}
                            >
                                SELECTED VITALS
                            </p>
                            <div
                                className="flex flex-wrap items-center overflow-auto mt-48 mx-20 pb-20"
                                id={SELECTED_VITALS_ITEMS}
                            >
                                {vitalsCommonItems?.map((d) => (
                                    <span
                                        id={d.id}
                                        key={d.id}
                                        role="button"
                                        className="flex items-center mt-8 mr-8 tab-chip"
                                    >
                                        {d.name}
                                        <div
                                            className="flex justify-end items-center w-12 h-20 w-20"
                                            onClick={() => deleteVitalsCommonItemsElement(d.id)}
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="9"
                                                height="11"
                                                viewBox="0 0 9 11"
                                                fill="none"
                                                className="ml-8"
                                            >
                                                <path
                                                    d="M1.03516 9.37109C1.05469 9.85938 1.48438 10.25 1.97266 10.25H6.75781C7.24609 10.25 7.67578 9.85938 7.69531 9.37109L8.125 2.75H0.625L1.03516 9.37109ZM8.4375 0.875H6.25L6.01562 0.425781C5.95703 0.328125 5.85938 0.25 5.74219 0.25H2.98828C2.87109 0.25 2.77344 0.328125 2.71484 0.425781L2.5 0.875H0.3125C0.136719 0.875 0 1.03125 0 1.1875V1.8125C0 1.98828 0.136719 2.125 0.3125 2.125H8.4375C8.59375 2.125 8.75 1.98828 8.75 1.8125V1.1875C8.75 1.03125 8.59375 0.875 8.4375 0.875Z"
                                                    fill="#B04E43"
                                                />
                                            </svg>
                                        </div>
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </Fragment>
        );
    }
}

export default PPVitalsConfigPanel;
